import React, { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowUpRightFromSquare, faCopy, faBolt, faComments, faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

/**
 * The small pieces both stock tabs draw with. Kept out of Stock.js so the
 * board and the catalogue can import them without importing each other.
 */

/** 12 not 12.00, 1.5 stays 1.5 — quantities are counted things, not money. */
export function formatQty(q) {
    const n = Number(q);
    if (!Number.isFinite(n)) return q;
    return Number.isInteger(n) ? String(n) : String(n).replace(/\.?0+$/, '');
}

/**
 * "today" / "3 days ago" — the only time reading that matters on this board is
 * how long something has been sitting, so that is what it says. An exact
 * timestamp is on the title attribute for the rare time it is needed.
 */
export function sinceLabel(iso) {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return '';
    const mins = Math.floor((Date.now() - then) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days} days ago`;
    const months = Math.floor(days / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
}

export function exactTime(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

/** Days waited, used to escalate the look of a row nobody has touched. */
export function daysSince(iso) {
    if (!iso) return 0;
    return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

/**
 * A button that shows it is working.
 *
 * WHY THIS EXISTS AS A COMPONENT. Requesting, ordering and receiving all hit
 * the network and then re-read the whole board, which is long enough to look
 * like nothing happened — and a second click on "Mark ordered" is a second
 * order. The button owns its own busy state so every action on this screen
 * behaves the same way without the parent tracking which row is in flight.
 *
 * It also guards against the unmount-after-await warning: by the time an
 * action resolves, the refresh it triggered may have replaced this very row.
 */
export function AsyncButton({ onClick, className, children, busyLabel, disabled, ...rest }) {
    const [busy, setBusy] = useState(false);
    const alive = useRef(true);
    React.useEffect(() => () => { alive.current = false; }, []);

    const run = useCallback(async (e) => {
        if (busy) return;
        setBusy(true);
        try {
            await onClick?.(e);
        } finally {
            if (alive.current) setBusy(false);
        }
    }, [busy, onClick]);

    return (
        <button type="button" className={className} onClick={run} disabled={busy || disabled} {...rest}>
            {busy ? (
                <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    {busyLabel ? <span>{busyLabel}</span> : null}
                </>
            ) : children}
        </button>
    );
}

export function CategoryFilter({ categories, value, onChange, extra }) {
    return (
        <div className="stk__filters">
            <div className="stk__chips">
                <button
                    type="button"
                    className={`stk__chip ${value === null ? 'is-active' : ''}`}
                    onClick={() => onChange(null)}
                >
                    All
                </button>
                {categories.map((c) => (
                    <button
                        key={c.id}
                        type="button"
                        className={`stk__chip ${value === c.id ? 'is-active' : ''}`}
                        onClick={() => onChange(c.id)}
                    >
                        {c.name}
                        {c.open_count > 0 && <span className="stk__chipCount">{c.open_count}</span>}
                    </button>
                ))}
            </div>
            {extra ? <div className="stk__filtersExtra">{extra}</div> : null}
        </div>
    );
}

export function UrgencyBadge({ urgency }) {
    if (urgency !== 'urgent') return null;
    return (
        <span className="stk__badge stk__badge--urgent">
            <FontAwesomeIcon icon={faBolt} /> Urgent
        </span>
    );
}

export function StatusPill({ status }) {
    const map = {
        requested: ['To order', 'warn'],
        ordered: ['On the way', 'info'],
        delivered: ['Delivered', 'ok'],
        cancelled: ['Cancelled', 'muted'],
    };
    const [label, tone] = map[status] || [status, 'muted'];
    return <span className={`stk__pill stk__pill--${tone}`}>{label}</span>;
}

/**
 * The buying links — the primary one first, then the alternatives.
 *
 * WHY THE ALTERNATIVES ARE VISIBLE, NOT HIDDEN BEHIND A MENU. The moment a
 * manager needs them is the moment the first shop is out of stock, and that is
 * exactly when nobody wants to go hunting through a dropdown. Three buttons,
 * all one click, is the entire point of storing three links.
 */
/** A wa.me link opens a chat, not a shop — it gets the icon that says so. */
const isChat = (url) => /(?:wa\.me|api\.whatsapp\.com|web\.whatsapp\.com)/i.test(url || '');

export function LinkButtons({ links, compact = false, onOpen }) {
    const list = (links || []).slice().sort((a, b) => a.position - b.position);
    if (!list.length) {
        return <span className="stk__noLinks">No buying link yet</span>;
    }

    const copy = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Link copied');
        } catch {
            toast.error('Could not copy that link');
        }
    };

    return (
        <div className={`stk__links ${compact ? 'stk__links--compact' : ''}`}>
            {list.map((l, idx) => (
                <span key={l.id || idx} className="stk__linkWrap">
                    <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`stk__link ${idx === 0 ? 'stk__link--primary' : ''}`}
                        title={l.url}
                        onClick={() => onOpen?.(l)}
                    >
                        <FontAwesomeIcon icon={isChat(l.url) ? faComments : faArrowUpRightFromSquare} />
                        <span className="stk__linkLabel">
                            {l.label || (isChat(l.url)
                                ? 'Message the supplier'
                                : idx === 0 ? 'Buy' : `Alternative ${idx}`)}
                        </span>
                        {l.price != null && (
                            <span className="stk__linkPrice">AED {Number(l.price).toFixed(2)}</span>
                        )}
                    </a>
                    <button
                        type="button"
                        className="stk__linkCopy"
                        title="Copy link"
                        aria-label={`Copy ${l.label || 'link'}`}
                        onClick={() => copy(l.url)}
                    >
                        <FontAwesomeIcon icon={faCopy} />
                    </button>
                </span>
            ))}
        </div>
    );
}
